import AutoLayoutSelector from "./AutoLayoutSelector";
import ColorInput from "./ColorInput";
import EmptyInputState from "./EmptyInputState";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { CaretDownIcon, EnterIcon, ResetIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../../../../ui/command";
import {
  ClickableIcon,
  ComboBoxOptionType,
  DropdownOptionType,
  TooltipProps,
  useComboboxContainer,
} from ".";
import {
  ArrowLeftIcon,
  ChevronRight,
  HomeIcon,
  LaptopIcon,
  MonitorIcon,
  MonitorSmartphoneIcon,
  MousePointerIcon,
  PlusIcon,
  PointerIcon,
  SearchIcon,
  SmartphoneIcon,
  TabletIcon,
} from "lucide-react";
import { NewPropertyState } from "@/components/RightStyle/usePropertyStateV2";
import ReactDOM from "react-dom";
import { Button } from "@/components/ui/button";

type ComboBoxMode =
  | {
      mode: "normal";
    }
  | {
      mode: "variantMenuAndCancel";
    }
  | {
      mode: "listVariants";
    }
  | {
      mode: "listInteractionStates";
    }
  | {
      mode: "selectedVariant";
      selectedVariant: string;
      fromMenu: "breakpoints" | "interactions";
    };

interface ComboBoxProps {
  options: ComboBoxOptionType[];
  isSet: boolean;
  onOptionSelected: (option: ComboBoxOptionType, variant?: string) => void;
  input: string;
  open: boolean;
  closeCombobox: () => void;
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  anchorRef: React.RefObject<HTMLElement>;
  comboBoxMode: ComboBoxMode;
  setComboBoxMode: (mode: ComboBoxMode) => void;
  valueByVariant: Record<
    string,
    {
      value: string;
      active: boolean;
      displayValue: string | null;
    }
  >;
  onClear: (variant?: string) => void;
  keepOpenOnSelect?: boolean;
}

const ComboBox: React.FC<ComboBoxProps> = ({
  options,
  isSet,
  onOptionSelected,
  input,
  open,
  closeCombobox,
  buildArbitraryClass,
  buildOptionPreview,
  anchorRef,
  comboBoxMode,
  setComboBoxMode,
  valueByVariant,
  onClear,
  keepOpenOnSelect,
}) => {
  const { containerRef } = useComboboxContainer();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const selectedVariantInputRef = useRef<HTMLInputElement>(null);

  const [firstNonNullInput, setFirstNonNullInput] = useState<string | null>(
    null
  );
  const [firstNonNullComboBoxInput, setFirstNonNullComboBoxInput] = useState<
    string | null
  >(null);
  const [inputChanged, setInputChanged] = useState(false);
  const activeOptionRef = useRef<HTMLDivElement>(null);
  const comboboxRef = useRef(null);

  const activeVariant = useMemo(() => {
    return Object.keys(valueByVariant).find(
      (variant) => valueByVariant[variant].active
    );
  }, [valueByVariant]);

  const [comboboxStyle, setComboboxStyle] = useState<{
    top?: string;
    bottom?: string;
    left?: string;
    width?: string;
  } | null>(null);

  const [comboBoxInput, setComboBoxInput] = useState<string>("");

  useEffect(() => {
    const root = document.getElementById("styling-panel-portal-root");
    setPortalRoot(root);
  }, []);

  useEffect(() => {
    if (open && anchorRef.current && containerRef.current) {
      const inputRect = anchorRef.current.getBoundingClientRect();

      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceAbove = inputRect.top;
      const spaceBelow = viewportHeight - inputRect.bottom;

      const useBottomPosition = spaceBelow > spaceAbove;

      const position = useBottomPosition
        ? { top: `${inputRect.bottom}px` }
        : { bottom: `${viewportHeight - inputRect.top}px` };

      let width = containerRect.width;
      if (comboBoxMode.mode === "listVariants") {
        width = Math.max(width, 320);
      }
      // If the width plus left is off the screen, move it to the left
      const viewportWidth = window.innerWidth;
      const wouldOverflowRight = containerRect.left + width > viewportWidth;
      let left = `${containerRect.left}px`;
      if (wouldOverflowRight) {
        left = `${viewportWidth - width - 20}px`;
      }

      setComboboxStyle({
        ...position,
        left,
        width: `${width}px`,
      });
    }
  }, [open, anchorRef, containerRef, comboBoxMode?.mode]);

  useEffect(() => {
    // Reset whenever the mode changes
    setFirstNonNullInput(null);
    setFirstNonNullComboBoxInput(null);
  }, [comboBoxMode]);

  /**
   * Tracks the first non-null input (ie. the initial value.)
   *
   * Tracks changes to input and sets input changes.
   */
  useEffect(() => {
    if (input !== null && firstNonNullInput === null) {
      setFirstNonNullInput(input); // Store the first non-null input
    }
    if (firstNonNullInput !== null && input !== firstNonNullInput) {
      setInputChanged(true); // Mark as changed if different from first non-null input
    }
  }, [input]);

  /**
   * Do the same for the comboBoxInput
   */
  useEffect(() => {
    if (comboBoxInput !== null && firstNonNullComboBoxInput === null) {
      setFirstNonNullComboBoxInput(comboBoxInput); // Store the first non-null input
    }
    if (
      firstNonNullComboBoxInput !== null &&
      comboBoxInput !== firstNonNullComboBoxInput
    ) {
      setInputChanged(true); // Mark as changed if different from first non-null input
    }
  }, [comboBoxInput]);

  /**
   * If there was a non-null input, and it's now empty, reset the active index.
   */
  useEffect(() => {
    if (input === "" && firstNonNullInput !== null) {
      setActiveIndex(null);
    }
  }, [input, firstNonNullInput]);

  useEffect(() => {
    if (comboBoxInput === "" && firstNonNullComboBoxInput !== null) {
      setActiveIndex(null);
    }
  }, [comboBoxInput, firstNonNullComboBoxInput]);

  const filteredOptions = useMemo(() => {
    const inputToFilterBy =
      comboBoxMode.mode === "selectedVariant" ? comboBoxInput : input;

    let newOptions = options
      .map((option) => {
        let score = 0;

        if (!inputToFilterBy) {
          score = 1; // So that all items are considered when there's no searchTerm
        } else {
          const term = inputToFilterBy.toLowerCase();

          if (option.tailwind.toLowerCase() === term) {
            score += 10;
          } else if (option.tailwind.toLowerCase().startsWith(term)) {
            score += 5;
          } else if (option.tailwind.toLowerCase().includes(term)) {
            score += 3;
          }

          if (option.css.toLowerCase().includes(term)) {
            score += 1;
          }
        }

        return { ...option, score };
      })
      .sort((a, b) => b.score - a.score) // Sort items with higher scores first
      .filter((item) => item.score > 0); // Filter out non-matching items

    if (buildArbitraryClass && inputToFilterBy.length) {
      newOptions.unshift({
        ...buildArbitraryClass(inputToFilterBy),
        score: Number.MAX_SAFE_INTEGER,
      });
    }

    return newOptions;
  }, [
    input,
    comboBoxInput,
    comboBoxMode,
    open,
    options,
    buildArbitraryClass,
    inputChanged,
    firstNonNullInput,
    firstNonNullComboBoxInput,
  ]);

  const showAllOptionsDefault = firstNonNullInput !== null && !inputChanged;
  const showAllComboBoxOptions =
    firstNonNullComboBoxInput !== null && !inputChanged;

  const showAllOptions =
    comboBoxMode.mode === "selectedVariant"
      ? showAllComboBoxOptions
      : showAllOptionsDefault;

  const optionsToShow = showAllOptions ? options : filteredOptions;
  const defaultIndex =
    filteredOptions.length > 1
      ? options.findIndex(
          (value: ComboBoxOptionType) =>
            value.tailwind === filteredOptions[1].tailwind
        )
      : -1;

  const indexOfActiveOption =
    filteredOptions.length > 1
      ? optionsToShow.findIndex(
          (value: ComboBoxOptionType) =>
            value.tailwind === filteredOptions[1].tailwind
        )
      : 0;

  const optionThatIsActiveRef = useRef<ComboBoxOptionType | null>(null);
  useEffect(() => {
    optionThatIsActiveRef.current =
      activeIndex !== null ? optionsToShow[activeIndex] : null;
  }, [activeIndex, optionsToShow]);

  useEffect(() => {
    if (
      comboBoxMode.mode === "selectedVariant" &&
      selectedVariantInputRef.current
    ) {
      if (
        comboBoxMode.selectedVariant &&
        valueByVariant[comboBoxMode.selectedVariant]
      ) {
        setComboBoxInput(
          valueByVariant[comboBoxMode.selectedVariant].displayValue || ""
        );
      } else {
        setComboBoxInput("");
      }

      selectedVariantInputRef.current.focus();
    }
  }, [comboBoxMode.mode]);

  /**
   * Reset first non-null input and input change state on close.
   */
  useEffect(() => {
    if (open && showAllOptions && activeIndex === null && defaultIndex >= 0) {
      setActiveIndex(defaultIndex);
    }

    if (!open) {
      setFirstNonNullInput(null);
      setFirstNonNullComboBoxInput(null);
      setInputChanged(false);
    }
  }, [open, showAllOptions, defaultIndex]);

  useEffect(() => {
    if (input !== null && input !== "" && firstNonNullInput !== null) {
      setActiveIndex(indexOfActiveOption);
    }
  }, [input]);

  useEffect(() => {
    if (
      comboBoxInput !== null &&
      comboBoxInput !== "" &&
      firstNonNullComboBoxInput !== null
    ) {
      setActiveIndex(indexOfActiveOption);
    }
  }, [comboBoxInput]);

  const handleOptionSelected = (option: ComboBoxOptionType) => {
    const selectedVariant =
      comboBoxMode.mode === "selectedVariant"
        ? comboBoxMode.selectedVariant
        : activeVariant;

    onOptionSelected(option, selectedVariant);

    if (comboBoxMode.mode === "selectedVariant") {
      setComboBoxMode({ mode: "listVariants" });
    } else if (!keepOpenOnSelect) {
      closeCombobox();
    }
  };

  /**
   * Click-away effect
   */
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        comboboxRef.current &&
        // @ts-expect-error TS2339
        !comboboxRef.current.contains(event.target as Node)
      ) {
        // Also apply whatever the current value is. If empty, ignore
        if (input !== "" && optionThatIsActiveRef.current) {
          // handleOptionSelected(optionThatIsActiveRef.current);
        }

        closeCombobox();
        setActiveIndex(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [comboboxRef]);

  /**
   * Arrow and enter selection for combobox
   */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        // Move focus down
        setActiveIndex((prevIndex) =>
          prevIndex === null || prevIndex >= optionsToShow.length - 1
            ? 0
            : prevIndex + 1
        );
        event.preventDefault();
      } else if (event.key === "ArrowUp") {
        // Move focus up
        setActiveIndex((prevIndex) =>
          prevIndex === null || prevIndex <= 0
            ? optionsToShow.length - 1
            : prevIndex - 1
        );
        event.preventDefault();
      } else if (event.key === "Enter" && activeIndex !== null) {
        handleOptionSelected(optionsToShow[activeIndex]);
        event.preventDefault();
      } else if (event.key === "Escape") {
        closeCombobox();
        setActiveIndex(null);
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    activeIndex,
    optionsToShow,
    onOptionSelected,
    comboBoxMode,
    activeVariant,
  ]);

  /**
   * Scroll list to match activeIndex
   */
  useEffect(() => {
    if (activeOptionRef.current) {
      const activeElement = activeOptionRef.current;
      activeElement.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const content = () => {
    // Define breakpoints and interaction states separately
    const breakpointVariants = [
      {
        variant: "default",
        label: "default",
        desc: ">= 0rem (0px)",
        icon: <HomeIcon className="w-4 h-4 text-icons-secondary" />,
      },
      {
        variant: "sm",
        label: "sm",
        desc: ">= 40rem (640px)",
        icon: <SmartphoneIcon className="w-4 h-4 text-icons-secondary" />,
      },
      {
        variant: "md",
        label: "md",
        desc: ">= 48rem (768px)",
        icon: <TabletIcon className="w-4 h-4 text-icons-secondary" />,
      },
      {
        variant: "lg",
        label: "lg",
        desc: ">= 64rem (1024px)",
        icon: <LaptopIcon className="w-4 h-4 text-icons-secondary" />,
      },
      {
        variant: "xl",
        label: "xl",
        desc: ">= 80rem (1280px)",
        icon: <MonitorIcon className="w-4 h-4 text-icons-secondary" />,
      },
      {
        variant: "2xl",
        label: "2xl",
        desc: ">= 96rem (1536px)",
        icon: <MonitorIcon className="w-4 h-4 text-icons-secondary" />,
      },
    ];
    const interactionVariants = [
      {
        variant: "hover",
        label: "hover",
        desc: "apply on hover",
        icon: <MousePointerIcon className="w-4 h-4 text-icons-secondary" />,
      },
      {
        variant: "active",
        label: "active",
        desc: "apply on active",
        icon: <PointerIcon className="w-4 h-4 text-icons-secondary" />,
      },
      {
        variant: "focus",
        label: "focus",
        desc: "apply on focus",
        icon: <PointerIcon className="w-4 h-4 text-icons-secondary" />,
      },
      {
        variant: "dark",
        label: "dark",
        desc: "apply in dark mode",
        icon: (
          <svg
            className="w-4 h-4 text-icons-secondary"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        ),
      },
    ];

    if (
      comboBoxMode.mode === "normal" ||
      comboBoxMode.mode === "selectedVariant"
    ) {
      const variantOptionn = breakpointVariants.find(
        (bp) =>
          comboBoxMode.mode === "selectedVariant" &&
          bp.variant === comboBoxMode.selectedVariant
      );

      return (
        <div className="bg-popover shadow-md rounded-md  border flex flex-col">
          {comboBoxMode.mode === "selectedVariant" && (
            <>
              <div className="px-3 py-2 flex-grow flex flex-row justify-between space-x-1 items-center cursor-pointer">
                <div
                  className="flex flex-row space-x-2 items-center"
                  onClick={() => {
                    if (comboBoxMode.fromMenu === "breakpoints") {
                      setComboBoxMode({ mode: "listVariants" });
                    } else {
                      setComboBoxMode({ mode: "listInteractionStates" });
                    }
                  }}
                >
                  <div className="p-1 rounded-md bg-secondary-n-950">
                    <ArrowLeftIcon className="w-4 h-4 text-icons-secondary" />
                  </div>
                  <span className="text-xs font-medium text-white flex-1">
                    {variantOptionn ? (
                      <div className="flex flex-row space-x-2 items-center">
                        {variantOptionn.icon}
                        <span className={`text-xs font-medium text-white`}>
                          {variantOptionn.label}
                        </span>
                        <span className="text-xs text-secondary-n-500">
                          {variantOptionn.desc}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-row space-x-2 items-center">
                        <span className="text-xs font-medium text-white">
                          {comboBoxMode.selectedVariant}
                        </span>
                      </div>
                    )}
                  </span>
                </div>
              </div>
              <hr></hr>
              <div
                className={cn(
                  "flex flex-row m-2 rounded-md items-center px-1 border border-transparent bg-secondary-n-950 relative h-7 min-w-18"
                )}
              >
                <SearchIcon className="w-4 h-4 text-icons-secondary" />
                <input
                  ref={selectedVariantInputRef}
                  type="text"
                  placeholder="Search"
                  className="text-xs px-1 border-none outline-none w-full font-medium bg-transparent placeholder:text-secondary-n-600"
                  onChange={(e) => {
                    setComboBoxInput(e.target.value);
                  }}
                  value={comboBoxInput}
                />
                <Button
                  variant="secondary"
                  size="icon"
                  className="w-4 h-4"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear(comboBoxMode.selectedVariant);
                  }}
                >
                  <ResetIcon className="w-4 h-4" />
                </Button>
              </div>
              <hr></hr>
            </>
          )}
          <div className="max-h-72 overflow-y-auto flex flex-col hide-scrollbar">
            {optionsToShow.map((cls, index) => (
              <div
                key={index}
                className={`p-2 flex-grow flex flex-row justify-between text-2xs space-x-1 items-center cursor-pointer ${
                  index === activeIndex ? "bg-accent" : "hover:bg-accent"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionSelected(cls);
                }}
                ref={index === activeIndex ? activeOptionRef : null}
              >
                <div className="flex flex-row space-x-2 items-center">
                  {buildOptionPreview && (
                    <div className="w-4 h-4">{buildOptionPreview(cls)}</div>
                  )}
                  <div className="font-medium flex-grow whitespace-nowrap line-clamp-1">
                    {cls.tailwind}
                  </div>
                </div>
                <div className="flex flex-col flex-shrink">
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <div className="flex flex-col flex-shrink text-ellipsis overflow-hidden">
                        {cls.css
                          .split("\n")
                          .map((cssLine: string, index: number) => {
                            return (
                              <div
                                key={index}
                                className="font-light text-right flex-shrink text-secondary-n-500 line-clamp-1"
                              >
                                {cssLine}
                              </div>
                            );
                          })}
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="flex-grow p-0"
                      side="bottom"
                      avoidCollisions={false}
                    >
                      <div className="space-y-1 flex flex-col text-xs w-full overflow-hidden pt-4">
                        <h4 className="font-semibold px-4">{cls.tailwind}</h4>
                        <pre className="w-full overflow-auto px-4 pb-4">
                          <code className="css">{cls.css}</code>
                        </pre>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (comboBoxMode.mode === "variantMenuAndCancel") {
      return (
        <div className="bg-popover shadow-md rounded-md border flex flex-col hide-scrollbar">
          <div
            className="px-3 py-2 flex-grow flex flex-row justify-between space-x-1 items-center cursor-pointer hover:bg-accent"
            onClick={() => {
              setComboBoxMode({ mode: "listVariants" });
            }}
          >
            <div className="flex flex-row space-x-2 items-center">
              <MonitorSmartphoneIcon className="w-4 h-4 text-icons-secondary" />
              <span className="text-xs font-medium text-white flex-1">
                Responsive Breakpoints
              </span>
              {Object.keys(valueByVariant).filter((v) =>
                breakpointVariants.find((b) => b.variant === v)
              ).length > 0 && (
                <div className="rounded-xs py-[2px] px-[4px] text-primary bg-icons-accent w-[15px] h-[16px] flex items-center justify-center">
                  {
                    Object.keys(valueByVariant).filter((v) =>
                      breakpointVariants.find((b) => b.variant === v)
                    ).length
                  }
                </div>
              )}
            </div>
            <div>
              <ChevronRight className="w-4 h-4 text-icons-secondary" />
            </div>
          </div>
          {/* Interaction States menu entry */}
          <div
            className="px-3 py-2 flex-grow flex flex-row justify-between space-x-1 items-center cursor-pointer hover:bg-accent"
            onClick={() => {
              setComboBoxMode({ mode: "listInteractionStates" });
            }}
          >
            <div className="flex flex-row space-x-2 items-center">
              <PointerIcon className="w-4 h-4 text-icons-secondary" />
              <span className="text-xs font-medium text-white flex-1">
                Interaction States
              </span>
              {Object.keys(valueByVariant).filter((v) =>
                interactionVariants.find((i) => i.variant === v)
              ).length > 0 && (
                <div className="rounded-xs py-[2px] px-[4px] text-primary bg-icons-accent w-[15px] h-[16px] flex items-center justify-center">
                  {
                    Object.keys(valueByVariant).filter((v) =>
                      interactionVariants.find((i) => i.variant === v)
                    ).length
                  }
                </div>
              )}
            </div>
            <div>
              <ChevronRight className="w-4 h-4 text-icons-secondary" />
            </div>
          </div>
          {isSet && <hr></hr>}
          <div
            className={cn(
              "px-3 py-2 flex-grow flex flex-row justify-between space-x-1 items-center cursor-pointer hover:bg-accent",
              !isSet && "hidden"
            )}
            onClick={(e) => {
              e.stopPropagation();
              onClear(activeVariant);
            }}
          >
            <div className="flex flex-row space-x-2 items-center">
              <ResetIcon className="w-4 h-4" />
              <span className="text-xs font-medium text-white flex-1">
                Clear style
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (comboBoxMode.mode === "listVariants") {
      // Responsive Breakpoints list
      return (
        <div className="bg-popover rounded-md border border-secondary-n-800 shadow-lg min-w-[320px] overflow-hidden">
          <div
            className="px-3 py-2 flex-grow flex flex-row justify-between space-x-1 items-center cursor-pointer hover:bg-accent"
            onClick={() => {
              setComboBoxMode({ mode: "variantMenuAndCancel" });
            }}
          >
            <div className="flex flex-row space-x-2 items-center">
              <ArrowLeftIcon className="w-4 h-4 text-icons-secondary" />
              <span className="text-xs font-medium text-white flex-1">
                Back
              </span>
            </div>
          </div>
          <hr></hr>
          <div className="flex flex-col divide-y divide-secondary-n-800">
            {breakpointVariants.map((bp) => (
              <div
                key={bp.variant}
                className="px-3 py-2 flex-grow flex flex-row justify-between space-x-1 items-center cursor-pointer hover:bg-accent"
                onClick={() => {
                  setComboBoxMode({
                    mode: "selectedVariant",
                    selectedVariant: bp.variant,
                    fromMenu: "breakpoints",
                  });
                }}
              >
                <div className="flex flex-row space-x-2 items-center relative">
                  {bp.icon}
                  <div
                    className={cn(
                      "absolute left-[3px] top-[-1px] bg-background-CTA size-[6px] rounded-full",
                      valueByVariant[bp.variant] &&
                        !valueByVariant[bp.variant].active &&
                        "bg-white",
                      !valueByVariant[bp.variant] && "hidden"
                    )}
                  />
                  <span className={`text-xs font-medium text-white`}>
                    {bp.label}
                  </span>
                  <span className="text-xs text-secondary-n-500">
                    {bp.desc}
                  </span>
                </div>
                <div className="flex flex-row space-x-2 items-center">
                  {/* Show the value if set for this variant */}
                  {valueByVariant[bp.variant]?.value && (
                    <span className="text-white text-xs">
                      {valueByVariant[bp.variant].value}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-icons-secondary" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (comboBoxMode.mode === "listInteractionStates") {
      return (
        <div className="bg-popover rounded-md border border-secondary-n-800 shadow-lg min-w-[320px] overflow-hidden">
          <div
            className="px-3 py-2 flex-grow flex flex-row justify-between space-x-1 items-center cursor-pointer hover:bg-accent"
            onClick={() => {
              setComboBoxMode({ mode: "variantMenuAndCancel" });
            }}
          >
            <div className="flex flex-row space-x-2 items-center">
              <ArrowLeftIcon className="w-4 h-4 text-icons-secondary" />
              <span className="text-xs font-medium text-white flex-1">
                Back
              </span>
            </div>
          </div>
          <hr></hr>
          <div className="flex flex-col divide-y divide-secondary-n-800">
            {interactionVariants.map((iv) => (
              <div
                key={iv.variant}
                className="px-3 py-2 flex-grow flex flex-row justify-between space-x-1 items-center cursor-pointer hover:bg-accent"
                onClick={() => {
                  setComboBoxMode({
                    mode: "selectedVariant",
                    selectedVariant: iv.variant,
                    fromMenu: "interactions",
                  });
                }}
              >
                <div className="flex flex-row space-x-2 items-center relative">
                  {iv.icon}
                  <div
                    className={cn(
                      "absolute left-[3px] top-[-1px] bg-background-CTA size-[6px] rounded-full",
                      valueByVariant[iv.variant] &&
                        !valueByVariant[iv.variant].active &&
                        "bg-white",
                      !valueByVariant[iv.variant] && "hidden"
                    )}
                  />
                  <span className={`text-xs font-medium text-white`}>
                    {iv.label}
                  </span>
                  <span className="text-xs text-secondary-n-500">
                    {iv.desc}
                  </span>
                </div>
                <div className="flex flex-row space-x-2 items-center">
                  {/* Show the value if set for this variant */}
                  {valueByVariant[iv.variant]?.value && (
                    <span className="text-white text-xs">
                      {valueByVariant[iv.variant].value}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-icons-secondary" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const comboboxContent = (
    <div
      className="fixed flex-grow z-[100002] text-xs"
      ref={comboboxRef}
      style={comboboxStyle || {}}
      data-inside-modal="true"
    >
      <div className="p-2">{open && content()}</div>
    </div>
  );

  if (!comboboxStyle || filteredOptions.length === 0) {
    return null;
  }

  if (!portalRoot) {
    return comboboxContent;
  }

  return ReactDOM.createPortal(comboboxContent, portalRoot);
};

interface VariantIndicatorProps {
  propertyState: Partial<NewPropertyState> & {
    propertyValue: string;
    displayValue: string;
    isSet: boolean;
    valueByVariant: Record<
      string,
      {
        value: string;
        displayValue: string | null;
        active: boolean;
      }
    >;
  };
}

const VariantIndicator = ({ propertyState }: VariantIndicatorProps) => {
  return (
    <div
      className={cn(
        "absolute right-[-3px] top-[-3px] bg-background-CTA size-[6px] rounded-full",

        // If there are variants and none are active, show white dot
        Object.keys(propertyState.valueByVariant).length >= 1 &&
          Object.keys(propertyState.valueByVariant).filter(
            (variant) => propertyState.valueByVariant[variant].active
          ).length === 0 &&
          "bg-white",
        // If there are no variants and the style is not set, show no dot
        !propertyState.isSet &&
          Object.keys(propertyState.valueByVariant).length === 0 &&
          "hidden",
        // If the only variant is the default one, show no dot
        Object.keys(propertyState.valueByVariant).length === 1 &&
          propertyState.valueByVariant["default"] &&
          "hidden"
      )}
    />
  );
};

interface DraggableInputProps {
  propertyState: Partial<NewPropertyState> & {
    propertyValue: string;
    displayValue: string;
    isSet: boolean;
    valueByVariant: Record<
      string,
      {
        value: string;
        displayValue: string | null;
        active: boolean;
      }
    >;
  };
  labelContent?: ReactNode;
  comboboxOptions?: ComboBoxOptionType[];
  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  onComboboxSelected?: (option: ComboBoxOptionType, variant?: string) => void;
  onSliderValueUpdate?: (option: ComboBoxOptionType, variant?: string) => void; // Must be supplied with build arbitrary class fn
  buildArbitraryClass?: (input: string) => ComboBoxOptionType;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  wrap?: boolean;
  fallback?: { number: number; unit: string }; // Used if the value cannot be parsed
  draggable?: boolean;
  onClear: (variant?: string) => void;
  className?: string;
  placeholder?: string;
  isShowDropdownOnly?: boolean;
  showCombox?: boolean;
  hideComboxOptions?: (value: boolean) => void;
  inputClassName?: string;
  keepOpenOnSelect?: boolean;
}

const VariantDraggableInput: React.FC<DraggableInputProps & TooltipProps> = ({
  showCombox,
  propertyState,
  labelContent = null,
  dropdownOptions = [],
  comboboxOptions,
  onComboboxSelected,
  onSliderValueUpdate,
  onClear,
  buildArbitraryClass,
  buildOptionPreview,
  onInputFocus,
  disabled = false,
  min,
  max,
  step,
  wrap = false,
  draggable = true,
  tooltipContent = null,
  fallback,
  className,
  placeholder,
  isShowDropdownOnly = false,
  hideComboxOptions,
  inputClassName,
  keepOpenOnSelect,
}) => {
  const [snapshot, setSnapshot] = useState(
    propertyState.cssValue || propertyState.propertyValue
  );
  const [startVal, setStartVal] = useState<null | number>(null);
  const [dropdownVisible, setDropdownVisible] = useState<null | boolean>(false);
  const [comboboxOpen, setComboboxOpen] = useState<boolean>(false);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const totalDragRef = useRef<number>(0);
  const latestValueRef = useRef<string | null>(
    propertyState.cssValue || propertyState.propertyValue
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const draggableInputRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState(propertyState.displayValue);

  const activeVariant = useMemo(() => {
    return Object.keys(propertyState.valueByVariant).find(
      (variant) => propertyState.valueByVariant[variant].active
    );
  }, [propertyState.valueByVariant]);

  const enhancedDropdownOptions = useMemo(() => {
    if (propertyState.isSet) {
      const clearDropdownOption: DropdownOptionType = {
        label: "Clear style",
        value: "clear",
        icon: <ResetIcon />,
      };

      // Return a new array with the 'Clear' option appended
      return [...dropdownOptions, clearDropdownOption];
    }

    return dropdownOptions;
  }, [dropdownOptions, propertyState.isSet]);

  // Extract the number and unit from the string value.
  const extractNumberAndUnit = (val: string) => {
    const match = val.match(/^(-?\d+(\.\d+)?)([a-zA-Z%]*)$/);
    if (match) {
      return {
        number: parseFloat(match[1]),
        unit: match[3],
      };
    }

    if (fallback) {
      return fallback;
    }

    return null;
  };

  useEffect(() => {
    setInputValue(propertyState.displayValue);
  }, [propertyState.displayValue]);

  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = ev.target.value;
    setInputValue(currentValue);

    if (comboboxOptions && comboboxOptions.length > 0) {
      setComboboxOpen(true);
    }

    if (onSliderValueUpdate && buildArbitraryClass) {
      onSliderValueUpdate(
        buildArbitraryClass(currentValue || ""),
        activeVariant
      );
    }
  };

  const onStart = useCallback(
    (event: React.MouseEvent) => {
      setStartVal(event.clientX);
      setSnapshot(propertyState.cssValue || propertyState.propertyValue);
    },
    [propertyState.cssValue, propertyState.propertyValue]
  );

  const handleComboboxOptionClick = (
    option: ComboBoxOptionType,
    variant?: string
  ) => {
    if (onComboboxSelected) {
      onComboboxSelected(option, variant);
    }

    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") {
      return;
    }

    if (inputValue === "") {
      setComboboxOpen(false);
      hideComboxOptions?.(false);
      onClear(activeVariant);

      if (inputRef.current) {
        inputRef.current.blur();
      }
    } else if (onComboboxSelected && buildArbitraryClass && inputValue) {
      onComboboxSelected(buildArbitraryClass(inputValue), activeVariant);
      setComboboxOpen(false);
      hideComboxOptions?.(false);
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  /**
   * Draggable input effect
   */
  useEffect(() => {
    const onUpdate = (event: MouseEvent) => {
      if (startVal !== null) {
        const diff = event.clientX - startVal;
        totalDragRef.current += Math.abs(diff);

        const extracted = extractNumberAndUnit(snapshot);
        if (extracted) {
          let updatedNumber = extracted.number + diff * (step || 1);

          // Check if there is a min value and if the current value is below it
          if (typeof min === "number" && updatedNumber < min) {
            updatedNumber = wrap ? max || updatedNumber : min;
          }

          // Check if there is a max value and if the current value is above it
          if (typeof max === "number" && updatedNumber > max) {
            updatedNumber = wrap ? min || updatedNumber : max;
          }

          const updatedValue = `${updatedNumber}${extracted.unit}`;
          latestValueRef.current = updatedValue;
          if (buildArbitraryClass && onSliderValueUpdate) {
            onSliderValueUpdate(
              buildArbitraryClass(updatedValue),
              activeVariant
            );
          }
        }
      }
    };

    const onEnd = () => {
      if (totalDragRef.current > 5) {
        if (
          buildArbitraryClass &&
          onComboboxSelected &&
          latestValueRef.current
        ) {
          onComboboxSelected(
            buildArbitraryClass(latestValueRef.current),
            activeVariant
          ); // Using value, as it's the final value after dragging
        }
      }

      totalDragRef.current = 0;
      latestValueRef.current = null;
      setStartVal(null);
    };

    document.addEventListener("mousemove", onUpdate);
    document.addEventListener("mouseup", onEnd);
    return () => {
      document.removeEventListener("mousemove", onUpdate);
      document.removeEventListener("mouseup", onEnd);
    };
  }, [startVal, snapshot, activeVariant]);

  /**
   * Track changes to disabled to ensure an unusable input isn't in focus.
   */
  useEffect(() => {
    if (disabled) {
      setFocused(false);
      setHovered(false);
      setComboboxOpen(false);
      hideComboxOptions?.(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!focused) {
      setInputValue(propertyState.displayValue);
    }
  }, [propertyState.displayValue, focused]);

  const [comboBoxMode, setComboBoxMode] = useState<ComboBoxMode>({
    mode: "normal",
  });

  return (
    <>
      {/*
       // @ts-expect-error TS2322 */}
      <div
        data-tooltip-id="tooltip"
        data-tooltip-content={tooltipContent}
        data-tooltip-place="bottom"
        data-tooltip-offset={5}
        data-tooltip-hidden={comboboxOpen || dropdownVisible}
        ref={draggableInputRef}
      >
        <div
          className={cn(
            "flex flex-row items-center px-1 border border-transparent hover:border-secondary-n-600 relative bg-secondary-n-950 rounded-md h-7 min-w-18",
            focused && "!border-primary-0",
            className
          )}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onContextMenu={(e) => {
            e.preventDefault();
            if (enhancedDropdownOptions && enhancedDropdownOptions.length > 0) {
              setDropdownVisible(true);
            }
          }}
          onMouseDown={(event) => {
            if (event.button === 2) {
              // Right click
              event.preventDefault();
            }
          }}
        >
          {/* Dropdown content */}
          {labelContent && (
            <div
              onMouseDown={(event: React.MouseEvent) => {
                if (draggable) {
                  onStart(event);
                }
              }}
              className={`py-1 pl-1.5 pr-2 text-secondary-n-600  ${
                draggable ? "cursor-ew-resize" : ""
              } select-none min-w-2`}
            >
              {labelContent}
            </div>
          )}
          <input
            disabled={disabled}
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            onFocus={(event) => {
              if (propertyState.propertyValue && isShowDropdownOnly) return;
              event.target.select();
              if (isShowDropdownOnly && enhancedDropdownOptions?.length) {
                setDropdownVisible(true);
              } else {
                setComboBoxMode({ mode: "normal" });
                setComboboxOpen(true);
              }
              setFocused(true);

              if (onInputFocus) {
                onInputFocus(event);
              }
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => setFocused(false)}
            className={cn(
              "text-xs px-1 border-none outline-none w-full font-medium bg-transparent placeholder:text-secondary-n-600",
              {
                "text-secondary-n-400": disabled || !propertyState.isSet,
                "text-text-primary": !disabled && propertyState.isSet,
              },
              inputClassName
            )}
            ref={inputRef}
          />

          <div
            onClick={() => {
              setComboBoxMode({ mode: "variantMenuAndCancel" });
              setComboboxOpen(true);
            }}
          >
            <CaretDownIcon className="mr-1" />
          </div>
          <VariantIndicator propertyState={propertyState} />
        </div>
        {((comboboxOpen && comboboxOptions) || showCombox) && (
          <ComboBox
            setComboBoxMode={setComboBoxMode}
            isSet={propertyState.isSet}
            valueByVariant={propertyState.valueByVariant || {}}
            comboBoxMode={comboBoxMode}
            input={inputValue}
            options={comboboxOptions!}
            open={comboboxOpen || showCombox!}
            closeCombobox={() => {
              setComboboxOpen(false);
              hideComboxOptions?.(false);
            }}
            onOptionSelected={(option: ComboBoxOptionType, variant?: string) =>
              handleComboboxOptionClick(option, variant)
            }
            buildOptionPreview={buildOptionPreview}
            buildArbitraryClass={buildArbitraryClass}
            anchorRef={draggableInputRef as React.RefObject<HTMLElement>}
            onClear={(variant?: string) => {
              setComboboxOpen(false);
              onClear(variant);
            }}
            keepOpenOnSelect={keepOpenOnSelect}
          />
        )}
      </div>
    </>
  );
};

export { VariantDraggableInput };
